import {
  getAnalyticsItemType,
  AnalyticsEventName,
  AddToCartMethod,
} from '@mb/lib';
import { handleSystemErrorReport } from '@mb/lib/utils';
import { ProductSearchMaterial } from '../types/material';

interface GAEvent {
  event: string;
  list_id: string;
  list_name: string;
  ecommerce: {
    currency: string;
    value: number;
    items: Record<string, unknown>[];
    item_list_id?: string;
    item_list_name?: string;
  };
  method?: string;
  event_location?: string;
}

// This function checks if the material is a paint product type
export function getUrlParameter(name: string, customUrl: string | null = null) {
  name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
  const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
  const results = regex.exec(customUrl || window.location.search);
  return results === null
    ? ''
    : decodeURIComponent(results[1].replace(/\+/g, ' '));
}

export const getAnalyticsItemCategoriesByTaxonomy = (
  taxonomy: string[] = [],
) => {
  const itemCategory: string[] = [];
  const itemCategory2: string[] = [];
  const itemCategory3: string[] = [];

  taxonomy.forEach((value) => {
    const splittedTaxonomy = value.split('>') ?? [];
    if (splittedTaxonomy[0] && !itemCategory.includes(splittedTaxonomy[0])) {
      itemCategory.push(splittedTaxonomy[0]);
    }
    if (splittedTaxonomy[1] && !itemCategory2.includes(splittedTaxonomy[1])) {
      itemCategory2.push(splittedTaxonomy[1]);
    }
    if (splittedTaxonomy[2] && !itemCategory3.includes(splittedTaxonomy[2])) {
      itemCategory3.push(splittedTaxonomy[2]);
    }
  });
  return {
    item_category: itemCategory.join(', '),
    item_category2: itemCategory2.join(', '),
    item_category3: itemCategory3.join(', '),
  };
};

export const mapMaterialsForAnalytics = (
  data: ProductSearchMaterial[] = [],
  currentColor: string,
  itemListId: string,
  itemListName: string,
) => {
  const configLang =
    typeof ENVIRONMENT_REGION !== 'undefined' ? ENVIRONMENT_REGION : null;
  const currency = configLang === 'EU' ? 'EUR' : 'USD';

  const gaType = data?.map((item, index) => {
    const colorway = item.ssFacetColorFilter?.find(
      (color) => color.image_url === item.thumbnailImageUrl?.split('?')?.[0],
    );
    let itemColor = null;
    if (colorway != null) {
      itemColor = colorway?.color_name;
    }
    const finishesColor = item?.configData?.color;

    let itemId = item.sku;
    try {
      itemId = item?.configurations[0]?.items[0]?.sku;
    } catch (e) {
      console.log(e.message);
    }

    const finalObj: Record<string, unknown> = {
      item_id: itemId,
      item_name: item.name,
      item_brand: item.manufacturer,
      item_type: getAnalyticsItemType(item),
      ...getAnalyticsItemCategoriesByTaxonomy(item.taxonomy ?? []),
      index: item.gridIndex || index + 1,
      price: 0,
      quantity: 1,
      item_list_id: itemListId,
      item_list_name: itemListName,
      item_variant: finishesColor || currentColor || itemColor || '',
    };

    if (item.associatedItem != null) {
      finalObj['associated_item'] = item.associatedItem || '';
    }

    return finalObj;
  });

  return { gaType, currency };
};

export const pushSearchAnalyticsDataLayer = (
  payload: ProductSearchMaterial[] = [],
  currentColor = '',
  listId = 'visual_search',
  listTitle = 'Visual Search',
  eventName = 'view_item_list',
  eventLocation: string,
  isFromCatalog = false,
  isSearch = false,
) => {
  try {
    const currentQuery = getUrlParameter('q');
    let finalListId = listId;
    let finalListTitle = listTitle;

    if (isSearch || currentQuery?.length > 0) {
      finalListId = 'search_results_sampling';
      finalListTitle = 'Sampling search results';
    }

    const { gaType, currency } = mapMaterialsForAnalytics(
      payload,
      currentColor,
      finalListId,
      finalListTitle,
    );

    const gaEvent: GAEvent = {
      event: eventName,
      list_id: finalListId,
      list_name: finalListTitle,
      ecommerce: {
        currency,
        value: 0,
        items: gaType,
      },
    };

    if (eventName === AnalyticsEventName.ADD_TO_CART) {
      gaEvent['method'] =
        gaType.length > 1
          ? AddToCartMethod.MULTI_SELECT
          : AddToCartMethod.SINGLE;
    }

    if (eventLocation != null) {
      gaEvent['event_location'] = eventLocation;
    }
    if (listId != null && isFromCatalog) {
      gaEvent.ecommerce['item_list_id'] = finalListId;
    }
    if (listTitle != null && isFromCatalog) {
      gaEvent.ecommerce['item_list_name'] = finalListTitle;
    }

    mitGTM.sendEvent({ ecommerce: null });
    mitGTM.sendEvent(gaEvent);
    console.log(gaEvent);
  } catch (e) {
    console.log(e);
    handleSystemErrorReport(`Error pushSearchAnalyticsDataLayer: ${e.message}`);
  }
};

/**
 * Build URL params for Analytics tracking on PDP based on the search list
 * @param params
 * @return string
 */
export const buildAnalyticsParamsToPDP = (params: {
  product: any;
  itemListId?: string;
  itemListName?: string;
  index?: number;
}) => {
  const { product, itemListId, itemListName, index } = params;

  const urlParams = [];
  itemListId && urlParams.push(`itemListId=${itemListId}`);
  itemListName && urlParams.push(`itemListName=${itemListName}`);
  urlParams.push(`index=${product?.gridIndex ?? product?.index ?? index}`);

  return urlParams.join('&');
};

export const mapProductToAnalyticsEcommerceItem = (params: {
  product: ProductSearchMaterial;
  itemListId?: string;
  itemListName?: string;
  index?: number;
}) => {
  const { product, itemListId, itemListName, index } = params;

  const colorway = product.ssFacetColorFilter?.find(
    (color) => color.image_url === product.thumbnailImageUrl?.split('?')?.[0],
  );
  const itemColor = colorway != null ? colorway?.color_name : null;
  const finishesColor = product?.configData?.color;

  return {
    item_id: product.sku,
    item_name: product.name,
    item_brand: product.manufacturer,
    ...getAnalyticsItemCategoriesByTaxonomy(product.taxonomy),
    index: product.gridIndex ?? product.index ?? index,
    price: 0,
    quantity: 1,
    item_list_id: itemListId,
    item_list_name: itemListName,
    item_variant: finishesColor ?? itemColor ?? '',
  };
};
